/**
 * SkipLink - Jump to main content for keyboard users
 *
 * Keyboard users shouldn't have to Tab through the entire
 * navigation menu to get to the main content. This link
 * lets them skip right to it.
 *
 * @example
 * <SkipLink targetId="main-content" />
 * ...
 * <main id="main-content">...</main>
 */

import React from 'react'

interface SkipLinkProps {
  /** ID of the element to skip to */
  targetId: string
  /** Link text (for different languages) */
  children?: React.ReactNode
}

export function SkipLink({ targetId, children = 'Skip to main content' }: SkipLinkProps) {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    const target = document.getElementById(targetId)
    if (target) {
      target.focus()
      target.scrollIntoView()
    }
  }

  return (
    <a
      href={`#${targetId}`}
      onClick={handleClick}
      style={{
        position: 'absolute',
        top: '-40px',
        left: 0,
        background: 'var(--primary)',
        color: 'white',
        padding: '8px 16px',
        zIndex: 9999,
        textDecoration: 'none',
        fontWeight: 600,
        borderRadius: '0 0 4px 0',
        transition: 'top 0.2s ease',
      }}
      onFocus={(e) => {
        e.currentTarget.style.top = '0'
      }}
      onBlur={(e) => {
        e.currentTarget.style.top = '-40px'
      }}
    >
      {children}
    </a>
  )
}
